import React, { createContext, useContext, useState, useEffect } from "react";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";

interface ProposalFormDataContextType {
  teams: any[];
  proposalStatuses: any[];
  deals: any[];
  loading: boolean;
  error: string | null;
}

const ProposalFormDataContext = createContext<
  ProposalFormDataContextType | undefined
>(undefined);

interface ProposalFormDataProviderProps {
  children: React.ReactNode;
  isMainApiCompleted?: boolean;
  shouldLoad?: boolean;
  loadStatusesOnly?: boolean;
}

export const ProposalFormDataProvider: React.FC<
  ProposalFormDataProviderProps
> = ({ children, isMainApiCompleted = false, shouldLoad = true, loadStatusesOnly = false }) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [teams, setTeams] = useState<any[]>([]);
  const [proposalStatuses, setProposalStatuses] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusesLoaded, setStatusesLoaded] = useState(false);
  const [fullDataLoaded, setFullDataLoaded] = useState(false);

  useEffect(() => {
    if (!selectedTenantId || !isMainApiCompleted) return;

    if (shouldLoad && !fullDataLoaded) {
      setLoading(true);
      setError(null);

      const fetchFormData = async () => {
        try {
          const authHeaders = getAuthHeaders();
          const promises = [
            fetch(`${API_BASE_URL}/team/?tenant_id=${selectedTenantId}&response_mode=light`, {
              headers: authHeaders,
            }),

            fetch(
              `${API_BASE_URL}/proposal-status/?tenant_id=${selectedTenantId}&response_mode=light`,
              {
                headers: authHeaders,
              }
            ),

            fetch(`${API_BASE_URL}/deal/?tenant_id=${selectedTenantId}&is_archive=false&response_mode=light`, {
              headers: authHeaders,
            }),
          ];

          const responses = await Promise.all(promises);
          const results = await Promise.all(
            responses.map((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
          );

          setTeams(results[0].results || []);

          const sortedProposalStatuses = (results[1].results || []).sort(
            (a: any, b: any) =>
              a.proposal_status_order - b.proposal_status_order
          );
          setProposalStatuses(sortedProposalStatuses);
          setDeals(results[2].results || []);
          setStatusesLoaded(true);
          setFullDataLoaded(true);
        } catch (err) {
          console.error("Error fetching proposal form data:", err);
          setError(
            err instanceof Error ? err.message : "Failed to fetch form data"
          );
        } finally {
          setLoading(false);
        }
      };

      fetchFormData();
    } else if (loadStatusesOnly && !statusesLoaded && !fullDataLoaded) {
      setLoading(true);
      setError(null);

      const fetchStatuses = async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/proposal-status/?tenant_id=${selectedTenantId}&response_mode=light`,
            { headers: getAuthHeaders() }
          );
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          const sortedProposalStatuses = (data.results || []).sort(
            (a: any, b: any) =>
              a.proposal_status_order - b.proposal_status_order
          );
          setProposalStatuses(sortedProposalStatuses);
          setStatusesLoaded(true);
        } catch (err) {
          console.error("Error fetching proposal statuses:", err);
          setError(
            err instanceof Error ? err.message : "Failed to fetch proposal statuses"
          );
        } finally {
          setLoading(false);
        }
      };

      fetchStatuses();
    }
  }, [selectedTenantId, isMainApiCompleted, shouldLoad, loadStatusesOnly, fullDataLoaded, statusesLoaded]);

  const value: ProposalFormDataContextType = {
    teams,
    proposalStatuses,
    deals,

    loading,
    error,
  };

  return (
    <ProposalFormDataContext.Provider value={value}>
      {children}
    </ProposalFormDataContext.Provider>
  );
};

export const useProposalFormData = () => {
  const context = useContext(ProposalFormDataContext);
  if (context === undefined) {
    throw new Error(
      "useProposalFormData must be used within a ProposalFormDataProvider"
    );
  }
  return context;
};
